import { useState } from 'react';

/**
 * useSessionFilter – wie useState, aber der Wert wird in sessionStorage gespeichert.
 * Dadurch bleiben Filtereinstellungen beim Navigieren innerhalb der Session erhalten.
 *
 * @param key           Eindeutiger Schlüssel in sessionStorage
 * @param defaultValue  Fallback-Wert, wenn noch kein gespeicherter Wert existiert
 * @param forceValue    Optionaler Override-Wert (z. B. aus URL-Params), der den
 *                      gespeicherten Wert ignoriert, wenn er übergeben wird.
 */
export function useSessionFilter<T>(
  key: string,
  defaultValue: T,
  forceValue?: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    if (forceValue !== undefined) return forceValue;
    try {
      const stored = sessionStorage.getItem(key);
      if (stored !== null) return JSON.parse(stored) as T;
    } catch {
      // JSON-Fehler oder kein Storage-Zugriff – ignorieren
    }
    return defaultValue;
  });

  const setStateAndPersist = (value: T | ((prev: T) => T)) => {
    setState((prev) => {
      const next = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value;
      try {
        sessionStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Storage voll oder blockiert – ignorieren
      }
      return next;
    });
  };

  return [state, setStateAndPersist];
}
